import React, { useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, CircularProgress, Button } from '@material-ui/core';
import RoomIcon from '@material-ui/icons/Room';
import useMapInit from '../hooks/useMapInit';
import useMapLayers from '../hooks/useMapLayers';
import { getJobsiteCoords } from '../hooks/useJobsite';
import MapLayerControl from './MapLayerControl';
import MapQuickLinks from './MapQuickLinks';
import EditToolbar from './EditToolbar';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  map: {
    width: '100%',
    height: '100%',
  },
  emptyState: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #e8e4d9 0%, #d8d3c6 50%, #cdc7b8 100%)',
    pointerEvents: 'none',
    zIndex: 500,
  },
  emptyInner: {
    textAlign: 'center',
    color: '#8b8680',
    background: 'rgba(255,255,255,0.9)',
    padding: '16px 24px',
    borderRadius: 8,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 8, left: 12,
    background: 'rgba(255,255,255,0.9)',
    padding: '6px 12px',
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    zIndex: 1000,
  },
  placementOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1050,
    cursor: 'crosshair',
    background: 'transparent',
  },
  placementBanner: {
    position: 'absolute',
    top: 58,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1100,
    background: 'rgba(25, 118, 210, 0.95)',
    color: '#fff',
    padding: '8px 16px',
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
    fontSize: 13,
    fontWeight: 500,
  },
  cancelPlacementBtn: {
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.6)',
    textTransform: 'none',
    fontSize: 12,
    padding: '2px 10px',
  },
}));

/**
 * MapContainer — mounts Leaflet via useMapInit, syncs the layer control state,
 * and centers on the loaded jobsite.
 *
 * Refs are owned by the parent so hooks upstream (areas rendering, drawing) can use them.
 */
const MapContainer = ({
  jobsite,
  loading,
  mapRef,
  drawnItemsRef,
  tileLayerRef,
  editingShape,
  onConfirmEdit,
  onCancelEdit,
  onUndoEdit,
  placingItemId,
  placementPaused,
  placementLabel,
  onPlacementOverlayClick,
  onCancelPlacement,
}) => {
  const classes = useStyles();
  const containerRef = useRef(null);

  useMapInit(containerRef, { mapRef, drawnItemsRef, tileLayerRef });

  const {
    providers,
    currentProviderName,
    selectBasemap,
    activeOverlays,
    toggleOverlay,
    dataLayers,
    toggleDataLayer,
  } = useMapLayers(mapRef, tileLayerRef);

  // Center the map on the jobsite when it loads / changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !jobsite) return;
    const coords = getJobsiteCoords(jobsite);
    if (coords) {
      map.setView(coords, 18, { animate: true });
    }
  }, [jobsite, mapRef]);

  return (
    <div className={classes.root} data-tour="site-takeoff-map">
      <div ref={containerRef} className={classes.map} />

      <MapLayerControl
        providers={providers}
        currentProviderName={currentProviderName}
        onSelectBasemap={selectBasemap}
        activeOverlays={activeOverlays}
        onToggleOverlay={toggleOverlay}
        dataLayers={dataLayers}
        onToggleDataLayer={toggleDataLayer}
      />

      <MapQuickLinks
        jobsite={jobsite}
        mapRef={mapRef}
      />

      {editingShape && (
        <EditToolbar
          onSave={onConfirmEdit}
          onCancel={onCancelEdit}
          onUndo={onUndoEdit}
        />
      )}

      {placingItemId && (
        <>
          <div className={classes.placementBanner}>
            <RoomIcon style={{ fontSize: 18 }} />
            <span>
              {placementPaused ? 'Placement paused' : 'Click map to place'}
              {placementLabel ? `: ${placementLabel}` : ''}
            </span>
            <Button
              variant="outlined"
              size="small"
              className={classes.cancelPlacementBtn}
              onClick={onCancelPlacement}
            >
              Stop
            </Button>
          </div>
          <div
            className={classes.placementOverlay}
            style={placementPaused ? { pointerEvents: 'none' } : undefined}
            onClick={onPlacementOverlayClick}
          />
        </>
      )}

      {!jobsite && !loading && (
        <div className={classes.emptyState}>
          <div className={classes.emptyInner}>
            <Typography variant="body1" style={{ fontWeight: 500, marginBottom: 4 }}>
              No jobsite selected
            </Typography>
            <Typography variant="caption">
              Use the search at the top to pick a property
            </Typography>
          </div>
        </div>
      )}
      {loading && (
        <div className={classes.loadingOverlay}>
          <CircularProgress size={14} />
          <span>Loading jobsite…</span>
        </div>
      )}
    </div>
  );
};

export default MapContainer;
